// Commandes d'une boutique — source unique: /api/orders (voir backend/routes/orders.js).
// Aucune logique de stock côté frontend: la décrémentation se fait exclusivement
// côté serveur lors de convertOrderToSale (RPC convert_order_to_sale -> create_sale).
//
// Mission "Product Core #01": le flux en deux étapes (bouton "Confirmer" qui ne
// faisait QUE changer le statut, puis un second bouton "Transformer en vente"
// qui apparaissait seulement ensuite) s'est révélé être la cause probable du
// signalement "la comptabilité reste à 0" — une commande PENDING confirmée
// mais jamais convertie n'est, à raison, pas une vente (ni CA, ni stock, ni
// encaissement), mais rien ne le disait clairement à l'écran. Le backend
// (convert_order_to_sale) n'a jamais exigé le statut CONFIRMED — seul le
// frontend imposait cette étape intermédiaire. "Confirmer la vente" est donc
// maintenant l'action unique et immédiate depuis PENDING (et reste disponible
// pour d'anciennes commandes déjà CONFIRMED).
import React, { useCallback, useEffect, useState } from 'react';
import { CircleAlert, CheckCircle2, Loader2 } from 'lucide-react';
import { getOrders, updateOrderStatus, convertOrderToSale } from '../../services/apiClient';

const PAYMENT_METHODS = [
  { value: 'CASH', label: 'Espèces' },
  { value: 'ORANGE_MONEY', label: 'Orange Money' },
  { value: 'WAVE', label: 'Wave' },
  { value: 'BANK_TRANSFER', label: 'Virement' },
  { value: 'OTHER', label: 'Autre' }
];

export const ORDER_STATUS_LABELS = {
  PENDING: 'En attente',
  CONFIRMED: 'Confirmée',
  COMPLETED: 'Terminée',
  CANCELLED: 'Annulée'
};

const FILTERS = [
  { id: '', label: 'Toutes' },
  { id: 'PENDING', label: 'En attente' },
  { id: 'CONFIRMED', label: 'Confirmées' },
  { id: 'COMPLETED', label: 'Terminées' },
  { id: 'CANCELLED', label: 'Annulées' }
];

const STATUS_COLORS = {
  PENDING: '#f59e0b',
  CONFIRMED: '#3b82f6',
  COMPLETED: '#1B6B4A',
  CANCELLED: '#ef4444'
};

const money = (value) => `${Number(value || 0).toLocaleString('fr-FR')} FCFA`;

const Spinner = () => (
  <Loader2 size={15} aria-hidden="true" style={{ animation: 'nsara-spin 0.7s linear infinite' }} />
);

export default function OrdersPanel({ businessId, shopId, onConverted, onViewSale }) {
  const [orders, setOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busyId, setBusyId] = useState('');
  const [convertingOrderId, setConvertingOrderId] = useState('');
  const [amountReceivedInput, setAmountReceivedInput] = useState('');
  const [conversionPaymentMethod, setConversionPaymentMethod] = useState('CASH');
  const [justConverted, setJustConverted] = useState(null); // { orderId, sale }

  const load = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    setError('');
    try {
      const { orders: rows } = await getOrders({ businessId, shopId, status: statusFilter || undefined, limit: 100 });
      setOrders(rows || []);
    } catch (err) {
      setError(err?.message || 'Impossible de charger les commandes.');
    } finally {
      setLoading(false);
    }
  }, [businessId, shopId, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const startConvert = (order) => {
    setError('');
    setMessage('');
    setJustConverted(null);
    setConvertingOrderId(order.id);
    setAmountReceivedInput('');
    setConversionPaymentMethod('CASH');
  };

  const changeStatus = async (order, status) => {
    setBusyId(order.id);
    setError('');
    setMessage('');
    try {
      await updateOrderStatus(order.id, status);
      await load();
      setMessage(`Commande ${ORDER_STATUS_LABELS[status].toLowerCase()}.`);
    } catch (err) {
      setError(err?.message || 'Mise à jour impossible.');
    } finally {
      setBusyId('');
    }
  };

  const convert = async (order) => {
    const amountReceivedRaw = amountReceivedInput === '' ? order.subtotal_estimated : Number(amountReceivedInput);
    const amountReceived = Math.max(0, Number(amountReceivedRaw) || 0);
    setBusyId(order.id);
    setError('');
    setMessage('');
    try {
      // Le statut local n'est JAMAIS passé à COMPLETED avant la réponse serveur.
      const result = await convertOrderToSale(order.id, { amountPaid: amountReceived, paymentMethod: conversionPaymentMethod });
      await load();
      setConvertingOrderId('');
      setAmountReceivedInput('');
      setJustConverted({ orderId: order.id, sale: result?.sale });
      if (onConverted) await onConverted(result?.sale);
    } catch (err) {
      if (err?.code === 'INSUFFICIENT_STOCK') {
        setError('Stock insuffisant pour finaliser cette commande.');
      } else if (err?.code === 'ORDER_ALREADY_CONVERTED') {
        setError('Cette commande est déjà convertie en vente.');
        await load();
      } else if (err?.code === 'OVERPAYMENT') {
        setError('Le montant reçu dépasse le total de la commande.');
      } else {
        setError(err?.message || 'Conversion impossible.');
      }
    } finally {
      setBusyId('');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        {FILTERS.map((filter) => (
          <button
            key={filter.id || 'all'}
            onClick={() => setStatusFilter(filter.id)}
            style={{
              padding: '8px 14px',
              borderRadius: 999,
              border: statusFilter === filter.id ? '1px solid #1B6B4A' : '1px solid #e2e8f0',
              background: statusFilter === filter.id ? '#E7F3ED' : 'white',
              color: statusFilter === filter.id ? '#145A3D' : '#334155',
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'background 0.15s ease, border-color 0.15s ease'
            }}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {message && <div style={{ background: '#E7F3ED', color: '#145A3D', padding: 12, borderRadius: 10, marginBottom: 12 }}>{message}</div>}
      {error && <div role="alert" style={{ background: '#fee2e2', color: '#991b1b', padding: 12, borderRadius: 10, marginBottom: 12 }}>{error}</div>}

      {loading ? (
        <p style={{ color: '#64748b' }}>Chargement des commandes…</p>
      ) : orders.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#64748b', border: '2px dashed #e2e8f0', borderRadius: 16 }}>
          Aucune commande pour le moment.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {orders.map((order) => {
            const notYetSale = order.status === 'PENDING' || order.status === 'CONFIRMED';
            const isConverting = convertingOrderId === order.id;
            const isBusy = busyId === order.id;
            return (
            <div
              key={order.id}
              style={{
                background: 'white',
                border: '1px solid #e2e8f0',
                borderLeft: `4px solid ${STATUS_COLORS[order.status] || '#94a3b8'}`,
                borderRadius: 14,
                padding: 16,
                transition: 'border-color 0.2s ease'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                <div>
                  <strong style={{ fontSize: 16 }}>{order.order_number}</strong>
                  <span
                    data-testid={`order-status-${order.id}`}
                    style={{
                      marginLeft: 10,
                      padding: '4px 10px',
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      background: `${STATUS_COLORS[order.status] || '#94a3b8'}20`,
                      color: STATUS_COLORS[order.status] || '#475569'
                    }}
                  >
                    {ORDER_STATUS_LABELS[order.status] || order.status}
                  </span>
                  <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: 13 }}>
                    {new Date(order.created_at).toLocaleString('fr-FR')}
                  </p>
                  <p style={{ margin: '6px 0 0', fontSize: 14 }}>
                    {order.customer_name} · {order.customer_phone}
                  </p>
                  {order.delivery_address && (
                    <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 13 }}>{order.delivery_address}</p>
                  )}
                </div>
                <strong style={{ fontSize: 18, color: '#1B6B4A' }}>{money(order.subtotal_estimated)}</strong>
              </div>

              {notYetSale && !isConverting && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 14, padding: 12, background: '#FEF6E7', border: '1px solid #FBE7BE', borderRadius: 10 }}>
                  <CircleAlert size={18} color="#B45309" aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <strong style={{ color: '#92400E', fontSize: 13 }}>Commande en attente</strong>
                    <p style={{ margin: '2px 0 0', color: '#92400E', fontSize: 13 }}>Cette commande n'est pas encore comptabilisée : ni stock, ni chiffre d'affaires, ni encaissement tant qu'elle n'est pas transformée en vente.</p>
                  </div>
                </div>
              )}

              {order.status === 'COMPLETED' && justConverted?.orderId === order.id && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14, padding: 12, background: '#E7F3ED', border: '1px solid #BFE3CD', borderRadius: 10 }}>
                  <CheckCircle2 size={18} color="#145A3D" aria-hidden="true" style={{ flexShrink: 0 }} />
                  <div>
                    <strong style={{ color: '#145A3D', fontSize: 13 }}>Vente enregistrée</strong>
                    {Number(justConverted.sale?.remaining) > 0 && (
                      <p style={{ margin: '2px 0 0', color: '#92400E', fontSize: 13, fontWeight: 700 }}>
                        Reste à payer : {money(justConverted.sale.remaining)}
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14, paddingTop: 14, borderTop: '1px solid #f1f5f9' }}>
                {notYetSale && !isConverting && (
                  <>
                    <button onClick={() => startConvert(order)} disabled={isBusy} style={btnPrimary}>
                      Confirmer la vente
                    </button>
                    <button onClick={() => changeStatus(order, 'CANCELLED')} disabled={isBusy} style={btnDanger}>
                      {isBusy ? <Spinner /> : 'Annuler'}
                    </button>
                  </>
                )}
                {notYetSale && isConverting && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4 }}>Montant reçu</label>
                        <input
                          type="number" min={0}
                          aria-label="Montant reçu"
                          placeholder={`${money(order.subtotal_estimated)} (payé en totalité)`}
                          value={amountReceivedInput}
                          onChange={(e) => setAmountReceivedInput(e.target.value)}
                          disabled={isBusy}
                          style={{ padding: 10, borderRadius: 10, border: '1px solid #e2e8f0', width: 200 }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4 }}>Mode de paiement</label>
                        <select value={conversionPaymentMethod} onChange={(e) => setConversionPaymentMethod(e.target.value)} disabled={isBusy} style={{ padding: 10, borderRadius: 10, border: '1px solid #e2e8f0' }}>
                          {PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                        </select>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => convert(order)} disabled={isBusy} style={btnPrimary}>
                        {isBusy ? <><Spinner /> Conversion…</> : 'Confirmer la conversion'}
                      </button>
                      <button onClick={() => setConvertingOrderId('')} disabled={isBusy} style={btnDanger}>
                        Annuler
                      </button>
                    </div>
                  </div>
                )}
                {order.status === 'COMPLETED' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ color: '#145A3D', fontWeight: 700, fontSize: 14 }}>Vente enregistrée</span>
                    {order.sale_id && onViewSale && (
                      <button onClick={() => onViewSale(order.sale_id)} style={btnOutline}>Voir la vente</button>
                    )}
                  </div>
                )}
                {order.status === 'CANCELLED' && (
                  <span style={{ color: '#991b1b', fontWeight: 700, fontSize: 14 }}>Annulée</span>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const btnPrimary = {
  border: 'none',
  borderRadius: 10,
  padding: '9px 16px',
  background: '#1B6B4A',
  color: 'white',
  fontWeight: 700,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  transition: 'background 0.15s ease, opacity 0.15s ease'
};

const btnDanger = {
  border: '1px solid #fecaca',
  borderRadius: 10,
  padding: '9px 16px',
  background: '#fff1f2',
  color: '#b91c1c',
  fontWeight: 700,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  transition: 'background 0.15s ease, opacity 0.15s ease'
};

const btnOutline = {
  border: '1px solid #1B6B4A',
  borderRadius: 10,
  padding: '9px 16px',
  background: 'white',
  color: '#1B6B4A',
  fontWeight: 700,
  cursor: 'pointer',
  transition: 'background 0.15s ease'
};
